import { createGlobalState, useLocalStorage } from "@vueuse/core"
import { useSound } from "@vueuse/sound"
import { computed, watch } from "vue"
import bellSound from "@/assets/bell.mp3"

const STORAGE_KEY = "dc_backoffice_sounds_enabled"

export const useNotificationSound = createGlobalState(() => {
  const enabled = useLocalStorage<boolean>(STORAGE_KEY, true)

  const { play: rawPlay, sound, stop } = useSound(bellSound, {
    volume: 0.5,
    interrupt: true,
  })

  const isEnabled = computed({
    get: () => enabled.value,
    set: value => {
      enabled.value = value
    },
  })

  // Log playback errors for debugging, but do not auto-disable sounds.
  watch(
    sound,
    newSound => {
      if (!newSound) return
      newSound.on("playerror", (_id: unknown, err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn("⚠️ [NotificationSound] playerror received:", err)
      })
    },
    { immediate: true }
  )

  const play = () => {
    if (!isEnabled.value) return
    rawPlay()
  }

  const toggle = () => {
    isEnabled.value = !isEnabled.value
  }

  const enable = () => {
    isEnabled.value = true
  }

  const disable = () => {
    isEnabled.value = false
  }

  /**
   * Prime sound on a user gesture to satisfy autoplay policies.
   * Should be called from within a click/touch handler.
   */
  const prime = async () => {
    try {
      const wasEnabled = isEnabled.value
      if (!wasEnabled) {
        isEnabled.value = true
      }
      // Play once to unlock audio context, then stop quickly.
      rawPlay()
      // Small timeout to ensure Howler has started playback before stopping.
      setTimeout(() => {
        try {
          stop()
        } catch {
          // ignore
        }
        if (!wasEnabled) {
          isEnabled.value = false
        }
      }, 150)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [NotificationSound] Error during prime():", err)
    }
  }

  return {
    enabled: isEnabled,
    play,
    toggle,
    enable,
    disable,
    prime,
  }
})

// Helper for non-setup usage (e.g. WebSocket service)
export function playNotificationSound() {
  const { play } = useNotificationSound()
  play()
}

// Helper for priming from outside setup (used in App.vue)
export function primeNotificationSound() {
  const { prime } = useNotificationSound()
  prime()
}

